import { H1Gigaphone } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { dateToHumanTimeWithoutUtcOffset } from '@e3dc-react/shell/Libs/date'
import { Box, Button, SxProps, Theme, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import { getReactMarkdownComponents } from '../../config'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'

import { getTransformedMarkdown } from '../../transformers/get-transformed-markdown'

interface Props {
  id: number
  text: string
  startAt: Date | null
  title: string
  onMarkNotificationsAsRead: (id: number) => void
  sxStyle?: SxProps<Theme>
}

/**
 * Displays one notification with text and the option to mark the notification as read
 */
const NotificationItem: React.VFC<Props> = ({ id, text, startAt, title, onMarkNotificationsAsRead, sxStyle }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 'solid',
        borderWidth: '1px',
        borderColor: theme.palette.grey.grey06,
        backgroundColor: theme.palette.grey.grey07,
        padding: theme.spacing(2),
        ...sxStyle,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <H1Gigaphone color="primary" />
        <Typography sx={{ paddingRight: theme.spacing(2), paddingLeft: theme.spacing(2) }}>
          {startAt && dateToHumanTimeWithoutUtcOffset(startAt, t('common.format.date-fns-date'))}
        </Typography>
        <Typography>{title}</Typography>
      </Box>
      <Box>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} components={reactMarkdownComponents}>
          {getTransformedMarkdown(text)}
        </ReactMarkdown>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: theme.spacing(2) }}>
        <Button type="button" variant="outlined" onClick={() => onMarkNotificationsAsRead(id)}>
          {t('notification.action.mark-as-read')}
        </Button>
      </Box>
    </Box>
  )
}

export default NotificationItem
